import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  TextField
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { PageHeader } from '../components/ui';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';

const PAYMENT_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'online', label: 'Online' }
];

const emptyForm = {
  name: '',
  receiptFor: '',
  totalParticipants: '1',
  amount: '',
  paymentMode: 'cash',
  paymentId: ''
};

export default function ManualReceiptForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);

  const { data: nextIdRes } = useQuery(['admin-manual-receipt-next-id'], () => adminAPI.getNextManualReceiptId());
  const nextId = nextIdRes?.data?.data?.nextId;

  const createMutation = useMutation((body) => adminAPI.createManualReceipt(body), {
    onSuccess: (res) => {
      queryClient.invalidateQueries('admin-manual-receipts');
      queryClient.invalidateQueries('admin-manual-receipt-next-id');
      const created = res?.data?.data;
      showToast({
        message: created?.id ? `Manual receipt #${created.id} saved.` : 'Manual receipt saved.',
        severity: 'success'
      });
      navigate('/manual-receipts');
    },
    onError: (err) => {
      showToast({
        message: err.response?.data?.message || 'Failed to save manual receipt.',
        severity: 'error'
      });
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      name: form.name.trim(),
      receiptFor: form.receiptFor.trim(),
      totalParticipants: Number(form.totalParticipants),
      amount: Number(form.amount),
      paymentMode: form.paymentMode,
      paymentId: form.paymentId.trim()
    });
  };

  return (
    <div className="registrations-page">
      <Button component={Link} to="/manual-receipts" startIcon={<ArrowBackRoundedIcon />} sx={{ mb: 1.5 }}>
        Back to Manual Receipts
      </Button>

      <PageHeader
        title="Add Receipt"
        subtitle={`Receipt ID is assigned automatically${nextId ? ` (next: ${nextId})` : ''}.`}
      />

      <Card sx={{ maxWidth: 720 }}>
        <CardContent sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2.25 }}>
            <TextField
              label="Name"
              required
              fullWidth
              size="small"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Receipt For (i.e. Camp/Trek name)"
                required
                fullWidth
                size="small"
                value={form.receiptFor}
                onChange={(e) => setForm((f) => ({ ...f, receiptFor: e.target.value }))}
              />
              <TextField
                label="Total Participants"
                required
                type="number"
                size="small"
                inputProps={{ min: 1, step: 1 }}
                fullWidth
                value={form.totalParticipants}
                onChange={(e) => setForm((f) => ({ ...f, totalParticipants: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Amount"
                required
                type="number"
                size="small"
                inputProps={{ min: 1, step: '0.01' }}
                fullWidth
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              />
              <TextField
                select
                label="Payment Mode"
                required
                fullWidth
                size="small"
                value={form.paymentMode}
                onChange={(e) => setForm((f) => ({ ...f, paymentMode: e.target.value }))}
              >
                {PAYMENT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              label="Payment ID / Notes"
              fullWidth
              size="small"
              value={form.paymentId}
              onChange={(e) => setForm((f) => ({ ...f, paymentId: e.target.value }))}
            />
            <Box sx={{ pt: 0.5 }}>
              <Button type="submit" variant="contained" disabled={createMutation.isLoading} sx={{ minWidth: 120 }}>
                {createMutation.isLoading ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
}
